import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useRef, useState } from "react";
import clsx from "clsx";
import Input from "@/app/components/ui/Inputs/Input";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { useForm } from "react-hook-form";
import useFormValidations from "@/app/hooks/useFormValidations";
import { IPerSonaProps, useUserPerSonaAPI } from "@/app/hooks/api_hooks/customer_profile/useUserPerSonaAPI";
import { useUserPersonaDetailsAPI } from "@/app/hooks/api_hooks/customer_profile/useUserPersonaDetailsAPI";

export interface IPersonaAdd {
  persona: string;
}

export default function AddUserPerSona({ setOpen, selected, customerId }) {
  const { foruserPersonaCreation } = useFormValidations();
  const { execute: createPersona } = useUserPerSonaAPI();
  const { execute: fetchPersonas, personaTags, loading: personasLoading, fetched: personasFetched } = useUserPersonaDetailsAPI();
  const [showExistingPersonas, setShowExistingPersonas] = useState(false);

  const handleViewExistingPersonas = () => {
    setShowExistingPersonas(true);
    fetchPersonas(customerId);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const formHook = useForm<IPersonaAdd>({
    mode: "onChange",
    defaultValues: {
      persona: "",
    },
  });

  const cancelButtonRef = useRef(null);

  const onSubmit = (data: IPersonaAdd) => {
    const constructed: IPerSonaProps = {
      persona_tags: [data?.persona],
    };
    createPersona(constructed, customerId).then(({ status }) => {
      if (status) {
        setOpen(false);
      }
    });
  };

  return (
    <div>
      <Transition.Root show={true} as={Fragment}>
        <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 transition-opacity" />
          </Transition.Child>

          <div className="z-10 fixed inset-0 w-screen overflow-y-auto">
            <div className="flex justify-center items-end sm:items-center p-4 min-h-full text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative flex flex-col bg-white shadow-xl rounded-[12px] w-full max-w-[520px] max-h-[90vh] overflow-hidden text-left transition-all">
                  <form className="flex flex-col min-h-0" onSubmit={formHook.handleSubmit(onSubmit)}>
                    <div className="flex justify-between items-center px-6 py-5 border-[#EEE] border-b">
                      <div>
                        <h3 className="m-0 font-semibold text-black text-base">Add Persona</h3>
                        <p className="mt-1 mb-0 text-[#666] text-xs">Tag a persona against {selected}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="flex justify-center items-center bg-[#808DA01A] p-0 border-none rounded-full w-7 h-7 cursor-pointer"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex-1 space-y-5 px-6 py-5 overflow-y-auto">
                      <div className="flex justify-between items-center">
                        <h4 className="font-medium text-[#666] text-xs">Client Name</h4>
                        <p className="text-black text-sm">{selected}</p>
                      </div>

                      <div>
                        <h4 className="mb-2 font-medium text-[#666] text-xs">Persona</h4>
                        <Input
                          className="text-xs"
                          placeholder="Enter persona"
                          register={formHook.register(`persona`, {
                            required: true,
                            ...foruserPersonaCreation.validations,
                          })}
                          fieldError={formHook.formState.errors.persona}
                          errorMessages={[{ message: "", type: "required" }, foruserPersonaCreation.errors]}
                        />
                        <ul className={clsx("pt-3", formHook.formState.errors.persona && "mt-4")}>
                          <li className="text-[10px] before:content-['•'] before:mr-2">Must be continuous text</li>
                          <li className="text-[10px] before:content-['•'] before:mr-2">Cannot contain spaces</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="mb-2 font-medium text-[#666] text-xs">
                          Existing Persona
                          {showExistingPersonas && !personasLoading && personaTags?.length
                            ? ` (${personaTags.length})`
                            : ""}
                        </h4>
                        {!showExistingPersonas ? (
                          <button
                            type="button"
                            onClick={handleViewExistingPersonas}
                            className="text-xs font-medium text-[#193A6A] underline w-fit"
                          >
                            View Existing Persona
                          </button>
                        ) : personasLoading ? (
                          <p className="text-xs text-gray-500">Loading…</p>
                        ) : personaTags?.length ? (
                          // Capped at 6 lines (6 x leading-5) and scrolled internally, so a
                          // client with a long tag list can never push Cancel/Add below the
                          // fold. 6 lines clears the 99th percentile (22 tags / ~307 chars)
                          // without scrolling; only the rare tail (max seen: 66 tags) scrolls.
                          <div className="max-h-[120px] overflow-y-auto rounded border border-gray-200 bg-gray-50 px-2 py-1">
                            <p className="text-xs leading-5 break-words">{personaTags.join(", ")}</p>
                          </div>
                        ) : personasFetched ? (
                          <p className="text-xs text-gray-500">No persona tagged against this client yet.</p>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 bg-[#FAFAFA] px-6 py-4 border-[#EEE] border-t">
                      <button
                        type="button"
                        ref={cancelButtonRef}
                        onClick={() => setOpen(false)}
                        className={SECONDARY_BUTTON}
                      >
                        Cancel
                      </button>
                      <button type="submit" className={PRIMARY_BUTTON}>
                        Add
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
